import { computed } from '@legendapp/state';
import { globalState, UserProfile } from './globalState';
import { StateLogger } from '../utils/logger';

// Logger para os estados computados
const computedStateLogger = StateLogger.ComputedState;
let lastLoggedProfileState: string | null | undefined;

/**
 * Estado computado que combina o perfil do usuário e os dados de assinatura
 * para criar uma visualização consolidada para uso em componentes.
 * 
 * Este estado é atualizado automaticamente sempre que o perfil do usuário
 * ou os dados de assinatura mudam no estado global.
 */
export const userProfileViewModel = computed(() => {
    const profile = globalState.user.profile.get();
    const subscription = globalState.subscription.get();

    //computedStateLogger.debug('Recalculando userProfileViewModel');

    if (!profile) {
        if (lastLoggedProfileState !== null) {
            computedStateLogger.debug('Perfil não disponível, retornando null');
            lastLoggedProfileState = null;
        }
        return null;
    }

    // Derivar status ativo
    const isActive = subscription.status === 'ativa' || subscription.status === 'cancelamento_agendado';

    // Garantir valores default seguros para evitar undefined
    const planDetails = {
        planType: subscription.planType || 'free',
        planName: subscription.planName || '',
        isActive,
        nextBillingDate: subscription.nextBillingDate || null,
        expiration: subscription.expiration || null,
        planId: subscription.planId || '',
    };

    const logState = JSON.stringify([profile.id, profile.empresa_id, planDetails]);
    if (logState !== lastLoggedProfileState) {
        computedStateLogger.debug('userProfileViewModel atualizado', {
            userId: profile.id,
            empresaId: profile.empresa_id,
            ...planDetails,
        });
        lastLoggedProfileState = logState;
    }

    return {
        ...profile,
        planDetails
    };
});

/**
 * Retorna um objeto que indica se o usuário tem uma assinatura premium ativa
 * e outras informações úteis sobre status do plano.
 */
export const subscriptionStatusViewModel = computed(() => {
    const subscription = globalState.subscription.get();

    const isActive = subscription.status === 'ativa' || subscription.status === 'cancelamento_agendado';
    const isPremium = subscription.planType === 'premium';

    return {
        isPremium,
        planType: subscription.planType,
        planName: subscription.planName || '',
        isActive,
        nextBillingDate: subscription.nextBillingDate || null,
        expiration: subscription.expiration || null,
    };
});

/**
 * Retorna informações básicas do perfil apenas para exibição,
 * filtrando dados sensíveis ou desnecessários para a interface.
 */
export const userDisplayInfoViewModel = computed(() => {
    const profile = globalState.user.profile.get();

    if (!profile) return null;

    // Novo: fallback para local_path
    const photoUri = profile.photo_url || profile.photo_local_path || null;

    return {
        id: profile.id,
        name: profile.name || 'Usuário',
        photoUrl: photoUri,
        email: profile.email || '',
        isComplete: !!(profile.name && profile.email && profile.telefone),
    };
});

/**
 * Estado computado específico para a tela de perfil
 * Combina os dados do perfil e otimiza o acesso para evitar re-renderizações desnecessárias
 * Filtra apenas as propriedades que precisamos para reduzir recálculos desnecessários
 */
export const profileScreenViewModel = computed(() => {
    // Acessar propriedades específicas em vez do objeto inteiro para reduzir recálculos
    const profileId = globalState.user.profile.id.get();
    const profileName = globalState.user.profile.name.get();
    const profileEmail = globalState.user.profile.email.get();
    const profileTelefone = globalState.user.profile.telefone.get();
    const profilePhotoUrl = globalState.user.profile.photo_url.get();
    const profilePhotoLocalPath = globalState.user.profile.photo_local_path.get();
    const profileEmpresaId = globalState.user.profile.empresa_id.get();

    // Novo: monta a URI correta com fallback, priorizando imagem local
    const photoUri = profilePhotoLocalPath || profilePhotoUrl || null;

    // Acessar apenas as propriedades de assinatura que realmente usamos
    const subscriptionStatus = globalState.subscription.status.get();
    const subscriptionPlanType = globalState.subscription.planType.get();
    const subscriptionExpiration = globalState.subscription.expiration.get();
    const isActive = subscriptionStatus === 'ativa' || subscriptionStatus === 'cancelamento_agendado';

    //computedStateLogger.debug('Recalculando profileScreenViewModel');

    // Se o ID for null, o perfil ainda não existe
    if (!profileId) return null;

    return {
        personalInfo: {
            id: profileId,
            name: profileName || 'Usuário',
            email: profileEmail || '',
            telefone: profileTelefone || '',
            photoUrl: photoUri,
            hasPhoto: !!photoUri, // considera local_path também
        },
        subscription: {
            isActive,
            planType: subscriptionPlanType || 'free',
            isPremium: subscriptionPlanType === 'premium',
            expiration: subscriptionExpiration,
            expirationFormatted: subscriptionExpiration
                ? new Date(subscriptionExpiration).toLocaleDateString('pt-BR')
                : null,
        },
        company: {
            id: profileEmpresaId || null,
            hasCompany: !!profileEmpresaId,
        }
    };
});

/**
 * Estado computado específico para a empresa
 * Formata os dados para uso na UI e observa apenas as propriedades necessárias
 */
export const companyViewModel = computed(() => {
    // Acessar propriedades específicas em vez do objeto inteiro para reduzir recálculos
    const company = globalState.company.data.get();

    //computedStateLogger.debug('Recalculando companyViewModel');

    if (!company) return null;

    // Acessa as propriedades específicas para fina granularidade na reatividade
    const id = company.id;
    const nome = company.nome;
    const cnpj = company.cnpj;
    const email = company.email;
    const telefone = company.telefone;
    const logo_url = company.logo_url;
    const logo_local_path = company.logo_local_path;
    const plano = company.plano;
    const endereco = company.endereco;
    const cep = company.cep;
    const cidade = company.cidade;
    const uf = company.uf;
    const stripe_customer_id = company.stripe_customer_id;
    const num_fotos = company.num_fotos;

    // Monta a URI correta da logo com fallback (prioriza o caminho local para refletir alterações offline)
    const logoUri = logo_local_path || logo_url || null;

    return {
        id,
        nome: nome || 'Empresa',
        cnpj: cnpj || '',
        email: email || '',
        telefone: telefone || '',
        hasLogo: !!logoUri, // considera local_path também
        logoUrl: logoUri,
        plano: plano || 'free',
        endereco: endereco || '',
        cep: cep || '',
        cidade: cidade || '',
        uf: uf || '',
        stripe_customer_id: stripe_customer_id || '',
        num_fotos: num_fotos || 5
    };
});

export default {
    userProfileViewModel,
    subscriptionStatusViewModel,
    userDisplayInfoViewModel,
    profileScreenViewModel,
    companyViewModel
}; 